import { uphealOSColorValues, avatarAccentValues } from '../tokens';
import rgba from '../../functions/rgba';
import { DesignSystemColorMode } from '../types';
import {
    isCheckpointCustomTheme,
    isWhiteLabelExampleTheme,
} from '@upheal/design-system/theme/legacy/is-whitelabel';
import deepmerge from 'deepmerge';

const shadow: DesignSystemColorMode['specific']['shadow'] = {
    neutral: {
        strong: rgba('#030712', 0.16),
        medium: rgba('#1F2937', 0.12),
    },
    primary: {
        strong: rgba('#6927DA', 0.2),
        medium: rgba('#875BF7', 0.16),
    },
    critical: {
        strong: rgba('#BE123C', 0.2),
        medium: rgba('#E11D48', 0.16),
    },
};

const colors: DesignSystemColorMode = {
    fg: {
        neutral: {
            primary: uphealOSColorValues.neutral['800'],
            primaryHover: uphealOSColorValues.neutral['900'],
            primaryActive: uphealOSColorValues.neutral['950'],
            secondary: uphealOSColorValues.neutral['700'],
            secondaryHover: uphealOSColorValues.neutral['800'],
            secondaryActive: uphealOSColorValues.neutral['900'],
            tertiary: uphealOSColorValues.neutral['600'],
            tertiaryHover: uphealOSColorValues.neutral['700'],
            tertiaryActive: uphealOSColorValues.neutral['800'],
            disabled: uphealOSColorValues.neutral['500'],
        },
        accent1: {
            primary: uphealOSColorValues.accent1['300'],
            primaryHover: uphealOSColorValues.accent1['200'],
            primaryActive: uphealOSColorValues.accent1['100'],
            secondary: uphealOSColorValues.accent1['400'],
            secondaryHover: uphealOSColorValues.accent1['300'],
            secondaryActive: uphealOSColorValues.accent1['200'],
        },
        accent2: {
            primary: uphealOSColorValues.accent2['300'],
            primaryHover: uphealOSColorValues.accent2['200'],
            primaryActive: uphealOSColorValues.accent2['100'],
            secondary: uphealOSColorValues.accent2['400'],
            secondaryHover: uphealOSColorValues.accent2['300'],
            secondaryActive: uphealOSColorValues.accent2['200'],
        },
        positive: {
            primary: uphealOSColorValues.success['400'],
            primaryHover: uphealOSColorValues.success['300'],
            primaryActive: uphealOSColorValues.success['200'],
            secondary: uphealOSColorValues.success['500'],
            secondaryHover: uphealOSColorValues.success['400'],
            secondaryActive: uphealOSColorValues.success['300'],
        },
        attention: {
            primary: uphealOSColorValues.attention['400'],
            primaryHover: uphealOSColorValues.attention['300'],
            primaryActive: uphealOSColorValues.attention['200'],
            secondary: uphealOSColorValues.attention['500'],
            secondaryHover: uphealOSColorValues.attention['400'],
            secondaryActive: uphealOSColorValues.attention['300'],
        },
        critical: {
            primary: uphealOSColorValues.critical['400'],
            primaryHover: uphealOSColorValues.critical['300'],
            primaryActive: uphealOSColorValues.critical['200'],
            secondary: uphealOSColorValues.critical['500'],
            secondaryHover: uphealOSColorValues.critical['400'],
            secondaryActive: uphealOSColorValues.critical['300'],
        },
        inverse: {
            primary: uphealOSColorValues.neutral['100'],
            primaryHover: uphealOSColorValues.neutral['50'],
            primaryActive: uphealOSColorValues.neutral['0'],
            secondary: uphealOSColorValues.neutral['200'],
            secondaryHover: uphealOSColorValues.neutral['100'],
            secondaryActive: uphealOSColorValues.neutral['50'],
            tertiary: uphealOSColorValues.neutral['300'],
            tertiaryHover: uphealOSColorValues.neutral['200'],
            tertiaryActive: uphealOSColorValues.neutral['100'],
        },
        onAccent: {
            primary: uphealOSColorValues.alpha.white['900'],
            primaryHover: uphealOSColorValues.alpha.white['1000'],
            primaryActive: uphealOSColorValues.alpha.white['1000'],
            secondary: uphealOSColorValues.alpha.white['800'],
            secondaryHover: uphealOSColorValues.alpha.white['900'],
            secondaryActive: uphealOSColorValues.alpha.white['900'],
            tertiary: uphealOSColorValues.alpha.white['700'],
            tertiaryHover: uphealOSColorValues.alpha.white['800'],
            tertiaryActive: uphealOSColorValues.alpha.white['800'],
        },
    },
    bg: {
        neutral: {
            primary: uphealOSColorValues.neutral['0'],
            primaryHover: uphealOSColorValues.neutral['50'],
            primaryActive: uphealOSColorValues.neutral['100'],
            secondary: uphealOSColorValues.neutral['50'],
            secondaryHover: uphealOSColorValues.neutral['100'],
            secondaryActive: uphealOSColorValues.neutral['200'],
            tertiary: uphealOSColorValues.neutral['100'],
            tertiaryHover: uphealOSColorValues.neutral['200'],
            tertiaryActive: uphealOSColorValues.neutral['300'],
            quaternary: uphealOSColorValues.neutral['200'],
            quaternaryHover: uphealOSColorValues.neutral['300'],
            quaternaryActive: uphealOSColorValues.neutral['400'],
            disabled: uphealOSColorValues.neutral['100'],
        },
        accent1: {
            primary: uphealOSColorValues.accent1['700'],
            primaryHover: uphealOSColorValues.accent1['600'],
            primaryActive: uphealOSColorValues.accent1['500'],
            secondary: uphealOSColorValues.accent1['900'],
            secondaryHover: uphealOSColorValues.accent1['800'],
            secondaryActive: uphealOSColorValues.accent1['700'],
            tertiary: uphealOSColorValues.accent1['950'],
            tertiaryHover: uphealOSColorValues.accent1['900'],
            tertiaryActive: uphealOSColorValues.accent1['800'],
        },
        accent2: {
            primary: uphealOSColorValues.accent2['700'],
            primaryHover: uphealOSColorValues.accent2['600'],
            primaryActive: uphealOSColorValues.accent2['500'],
            secondary: uphealOSColorValues.accent2['900'],
            secondaryHover: uphealOSColorValues.accent2['800'],
            secondaryActive: uphealOSColorValues.accent2['700'],
            tertiary: uphealOSColorValues.accent2['950'],
            tertiaryHover: uphealOSColorValues.accent2['900'],
            tertiaryActive: uphealOSColorValues.accent2['800'],
        },
        positive: {
            primary: uphealOSColorValues.success['700'],
            primaryHover: uphealOSColorValues.success['600'],
            primaryActive: uphealOSColorValues.success['500'],
            secondary: uphealOSColorValues.success['900'],
            secondaryHover: uphealOSColorValues.success['800'],
            secondaryActive: uphealOSColorValues.success['700'],
            tertiary: uphealOSColorValues.success['950'],
            tertiaryHover: uphealOSColorValues.success['900'],
            tertiaryActive: uphealOSColorValues.success['800'],
        },
        attention: {
            primary: uphealOSColorValues.attention['600'],
            primaryHover: uphealOSColorValues.attention['500'],
            primaryActive: uphealOSColorValues.attention['400'],
            secondary: uphealOSColorValues.attention['800'],
            secondaryHover: uphealOSColorValues.attention['700'],
            secondaryActive: uphealOSColorValues.attention['600'],
            tertiary: uphealOSColorValues.attention['950'],
            tertiaryHover: uphealOSColorValues.attention['900'],
            tertiaryActive: uphealOSColorValues.attention['800'],
        },
        critical: {
            primary: uphealOSColorValues.critical['700'],
            primaryHover: uphealOSColorValues.critical['600'],
            primaryActive: uphealOSColorValues.critical['500'],
            secondary: uphealOSColorValues.critical['800'],
            secondaryHover: uphealOSColorValues.critical['700'],
            secondaryActive: uphealOSColorValues.critical['600'],
            tertiary: uphealOSColorValues.critical['950'],
            tertiaryHover: uphealOSColorValues.critical['900'],
            tertiaryActive: uphealOSColorValues.critical['800'],
        },
        inverse: {
            primary: uphealOSColorValues.neutral['950'],
            primaryHover: uphealOSColorValues.neutral['900'],
            primaryActive: uphealOSColorValues.neutral['800'],
            secondary: uphealOSColorValues.neutral['900'],
            secondaryHover: uphealOSColorValues.neutral['800'],
            secondaryActive: uphealOSColorValues.neutral['700'],
            tertiary: uphealOSColorValues.neutral['800'],
            tertiaryHover: uphealOSColorValues.neutral['700'],
            tertiaryActive: uphealOSColorValues.neutral['600'],
        },
    },
    border: {
        sidebarHeader: '#3D397F',
        neutral: {
            primary: uphealOSColorValues.neutral['200'],
            primaryHover: uphealOSColorValues.neutral['300'],
            primaryActive: uphealOSColorValues.neutral['400'],
            secondary: uphealOSColorValues.neutral['100'],
            secondaryHover: uphealOSColorValues.neutral['200'],
            secondaryActive: uphealOSColorValues.neutral['300'],
            tertiary: uphealOSColorValues.neutral['50'],
            tertiaryHover: uphealOSColorValues.neutral['100'],
            tertiaryActive: uphealOSColorValues.neutral['200'],
        },
        accent1: {
            primary: uphealOSColorValues.accent1['500'],
            primaryHover: uphealOSColorValues.accent1['400'],
            primaryActive: uphealOSColorValues.accent1['300'],
            secondary: uphealOSColorValues.accent1['600'],
            secondaryHover: uphealOSColorValues.accent1['500'],
            secondaryActive: uphealOSColorValues.accent1['400'],
            tertiary: uphealOSColorValues.accent1['700'],
            tertiaryHover: uphealOSColorValues.accent1['600'],
            tertiaryActive: uphealOSColorValues.accent1['500'],
        },
        accent2: {
            primary: uphealOSColorValues.accent2['500'],
            primaryHover: uphealOSColorValues.accent2['400'],
            primaryActive: uphealOSColorValues.accent2['300'],
            secondary: uphealOSColorValues.accent2['600'],
            secondaryHover: uphealOSColorValues.accent2['500'],
            secondaryActive: uphealOSColorValues.accent2['400'],
            tertiary: uphealOSColorValues.accent2['700'],
            tertiaryHover: uphealOSColorValues.accent2['600'],
            tertiaryActive: uphealOSColorValues.accent2['500'],
        },
        positive: {
            primary: uphealOSColorValues.success['500'],
            primaryHover: uphealOSColorValues.success['400'],
            primaryActive: uphealOSColorValues.success['300'],
            secondary: uphealOSColorValues.success['600'],
            secondaryHover: uphealOSColorValues.success['500'],
            secondaryActive: uphealOSColorValues.success['400'],
            tertiary: uphealOSColorValues.success['700'],
            tertiaryHover: uphealOSColorValues.success['600'],
            tertiaryActive: uphealOSColorValues.success['500'],
        },
        attention: {
            primary: uphealOSColorValues.attention['500'],
            primaryHover: uphealOSColorValues.attention['400'],
            primaryActive: uphealOSColorValues.attention['300'],
            secondary: uphealOSColorValues.attention['600'],
            secondaryHover: uphealOSColorValues.attention['500'],
            secondaryActive: uphealOSColorValues.attention['400'],
            tertiary: uphealOSColorValues.attention['700'],
            tertiaryHover: uphealOSColorValues.attention['600'],
            tertiaryActive: uphealOSColorValues.attention['500'],
        },
        critical: {
            primary: uphealOSColorValues.critical['500'],
            primaryHover: uphealOSColorValues.critical['400'],
            primaryActive: uphealOSColorValues.critical['300'],
            secondary: uphealOSColorValues.critical['600'],
            secondaryHover: uphealOSColorValues.critical['500'],
            secondaryActive: uphealOSColorValues.critical['400'],
            tertiary: uphealOSColorValues.critical['700'],
            tertiaryHover: uphealOSColorValues.critical['600'],
            tertiaryActive: uphealOSColorValues.critical['500'],
        },
        inverse: {
            primary: uphealOSColorValues.neutral['700'],
            primaryHover: uphealOSColorValues.neutral['800'],
            primaryActive: uphealOSColorValues.neutral['900'],
            secondary: uphealOSColorValues.neutral['600'],
            secondaryHover: uphealOSColorValues.neutral['700'],
            secondaryActive: uphealOSColorValues.neutral['800'],
            tertiary: uphealOSColorValues.neutral['500'],
            tertiaryHover: uphealOSColorValues.neutral['600'],
            tertiaryActive: uphealOSColorValues.neutral['700'],
        },
    },
    specific: {
        skeleton: {
            primary: uphealOSColorValues.neutral['100'],
        },
        charts: {
            primary: uphealOSColorValues.alpha.white['1000'],
        },
        shadow,
    },
    accents: avatarAccentValues,
    effects: {
        shadow: {
            neutral: {
                xs: `0px 1px 2px 0px ${shadow.neutral.strong}`,
                sm: `0px 2px 4px 0px ${shadow.neutral.strong}, 0px 1px 2px -1px ${shadow.neutral.strong}`,
                md: `0px 4px 8px -1px ${shadow.neutral.strong}, 0px 1px 2px 0px ${shadow.neutral.strong}`,
                lg: `0px 12px 16px -2px ${shadow.neutral.medium}, 0px 4px 8px -2px ${shadow.neutral.medium}, 0px 1px 2px -1px ${shadow.neutral.strong}`,
                xl: `0px 16px 24px -4px ${shadow.neutral.medium}, 0px 12px 20px -4px ${shadow.neutral.medium}, 0px 1px 2px -1px ${shadow.neutral.strong}`,
                '2xl': `0px 24px 40px -4px ${shadow.neutral.medium}, 0px 16px 24px 0px ${shadow.neutral.medium}, 0px 1px 2px -1px ${shadow.neutral.strong}`,
            },
            accent1: {
                xs: `0px 1px 2px 0px ${shadow.primary.strong}`,
                sm: `0px 2px 4px 0px ${shadow.primary.strong}, 0px 1px 2px -1px ${shadow.primary.strong}`,
                md: `0px 4px 8px -1px ${shadow.primary.strong}, 0px 1px 2px 0px ${shadow.primary.strong}`,
                lg: `0px 12px 16px -2px ${shadow.primary.medium}, 0px 4px 8px -2px ${shadow.primary.medium}, 0px 1px 2px -1px ${shadow.primary.strong}`,
                xl: `0px 16px 24px -4px ${shadow.primary.medium}, 0px 12px 20px -4px ${shadow.primary.medium}, 0px 1px 2px -1px ${shadow.primary.strong}`,
                '2xl': `0px 24px 40px -4px ${shadow.primary.medium}, 0px 16px 24px 0px ${shadow.primary.medium}, 0px 1px 2px -1px ${shadow.primary.strong}`,
            },
            critical: {
                xs: `0px 1px 2px 0px ${shadow.critical.strong}`,
                sm: `0px 2px 4px 0px ${shadow.critical.strong}, 0px 1px 2px -1px ${shadow.critical.strong}`,
                md: `0px 4px 8px -1px ${shadow.critical.strong}, 0px 1px 2px 0px ${shadow.critical.strong}`,
                lg: `0px 12px 16px -2px ${shadow.critical.medium}, 0px 4px 8px -2px ${shadow.critical.medium}, 0px 1px 2px -1px ${shadow.critical.strong}`,
                xl: `0px 16px 24px -4px ${shadow.critical.medium}, 0px 12px 20px -4px ${shadow.critical.medium}, 0px 1px 2px -1px ${shadow.critical.strong}`,
                '2xl': `0px 24px 40px -4px ${shadow.critical.medium}, 0px 16px 24px 0px ${shadow.critical.medium}, 0px 1px 2px -1px ${shadow.critical.strong}`,
            },
        },
        focusRing: {
            accent1: uphealOSColorValues.accent1['500'],
            critical: uphealOSColorValues.critical['500'],
        },
    },
};

if (isCheckpointCustomTheme) {
    Object.assign(colors, {
        fg: {
            accent1: {
                primary: uphealOSColorValues.neutral['950'],
                primaryHover: uphealOSColorValues.neutral['950'],
                primaryActive: uphealOSColorValues.neutral['950'],
            },
        },
        bg: {
            accent1: {
                primary: '#028ba6',
                primaryHover: '#028ba6',
                primaryActive: '#028ba6',
                secondary: '#028ba6',
                secondaryHover: '#028ba6',
                secondaryActive: '#028ba6',
                tertiary: '#028ba6',
                tertiaryHover: '#028ba6',
                tertiaryActive: '#028ba6',
            },
        },
        border: {
            accent1: {
                primary: '#028ba6',
                primaryHover: '#028ba6',
                primaryActive: '#028ba6',
                secondary: '#028ba6',
                secondaryHover: '#028ba6',
                secondaryActive: '#028ba6',
                tertiary: '#028ba6',
            },
        },
    });
}

if (isWhiteLabelExampleTheme) {
    Object.assign(
        colors,
        deepmerge(colors, {
            fg: {
                neutral: {
                    primary: '#e8e8f0',
                    secondary: '#c7c7dc',
                    tertiary: '#a2a3c0',
                },
            },
            bg: {
                neutral: {
                    primary: '#2a1a3a',
                    primaryHover: '#3a2a4a',
                    secondary: '#4a3a5a',
                    tertiary: '#5a4a6a',
                },
                accent1: {
                    primary: '#f00',
                    tertiary: '#a00',
                },
            },
            border: {
                neutral: {
                    primary: '#4a3a5a',
                    secondary: '#3a2a4a',
                },
            },
        }),
    );
}

export default colors;
